import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { approveDealWin, closeDeal, type CloseReasonCategory, type Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { DateRowInput } from "@/components/ui/date-row-input";
import { FormError } from "@/components/ui/form-error";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { apiErrorMessage } from "@/lib/api-error";
import { dollarsToCents } from "@/lib/currency";
import { CLOSE_REASON_LABELS } from "@/lib/deal-labels";

// Order shown in the "Reason for closure" dropdown; labels from the canonical
// CLOSE_REASON_LABELS map. "other" requires a note.
const CLOSE_REASONS: CloseReasonCategory[] = [
  "lost_to_competitor",
  "project_cancelled",
  "no_progress",
  "other",
];

/** Hold the success toast on screen before finishing — same 1.5s as the
 * registration decision actions (CHP-223/CHP-243). */
const REDIRECT_HOLD_MS = 1500;

/** Which close form is currently shown. `null` shows the two action buttons;
 * choosing an action swaps the buttons out for that action's form (CHP-405 —
 * dynamic swap, not a toggle-below). */
type CloseMode = null | "won" | "lost";

interface Props {
  deal: Deal;
  /** Channel partner (reseller) name — unused in the spec copy, kept for parity. */
  partnerName?: string | null;
  /** Called after a successful action (post toast + 1.5s hold). Drawer → close;
   * standalone route → navigate to the Deals Dashboard. */
  onDone: () => void;
}

/**
 * Manager-initiated close actions on a LIVE deal (CHP-243/CHP-255). A CM/RA/RM
 * either marks the deal Closed-Won directly (confirmed value + first-payment
 * date + PO number per CHP-277, routed through the win-approve transition) or
 * marks it Closed-Lost (reason for closure + additional details). Both hold the
 * toast 1.5s, then finish (redirect to the Deals Dashboard).
 *
 * CHP-405 (clean design): the action buttons and the chosen form share one
 * slot. Clicking "Mark as closed-won" / "Mark as closed-lost" REPLACES the
 * buttons with the matching form so focus stays singular and the action is
 * unambiguously attributed; the form's own "Cancel" restores the buttons.
 */
export function DealManagerCloseActions({ deal, onDone }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [mode, setMode] = React.useState<CloseMode>(null);
  const [contractValue, setContractValue] = React.useState("");
  const [paymentDate, setPaymentDate] = React.useState("");
  const [poNumber, setPoNumber] = React.useState("");
  const [closeReason, setCloseReason] = React.useState<CloseReasonCategory>("lost_to_competitor");
  const [closeNotes, setCloseNotes] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  // CHP-809: on success the form resets (setMode(null)) which restores the
  // trigger buttons while query invalidation/navigation is deferred for the
  // 1.5s hold. Keep the trays inert through that window so a second click can't
  // re-fire a close on the already-closed deal.
  const [finishing, setFinishing] = React.useState(false);

  const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  // CHP-805: move focus to the first field when the inline form opens, so a
  // keyboard/screen-reader user isn't stranded on the now-removed trigger.
  const formRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (mode === null) return;
    formRef.current?.querySelector<HTMLElement>("input, select, textarea")?.focus();
  }, [mode]);

  const holdThenDone = React.useCallback(() => {
    setFinishing(true);
    holdTimer.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      onDone();
    }, REDIRECT_HOLD_MS);
  }, [onDone, queryClient]);

  /** Restore the action buttons without losing the entered values. */
  const cancelForm = React.useCallback(() => {
    setError(null);
    setMode(null);
  }, []);

  const markWon = useMutation({
    mutationFn: () =>
      approveDealWin(accessToken!, deal.id, {
        confirmed_contract_value: dollarsToCents(contractValue),
        first_payment_due_date: paymentDate,
        po_number: poNumber.trim(),
      }),
    onSuccess: () => {
      setMode(null);
      toast(dealToasts.closedWon());
      holdThenDone();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Mark as closed-won failed. Check the values and try again.")),
  });

  const markLost = useMutation({
    mutationFn: () =>
      closeDeal(accessToken!, deal.id, {
        close_reason_category: closeReason,
        close_notes: closeNotes.trim() || null,
      }),
    onSuccess: () => {
      setMode(null);
      setCloseNotes("");
      toast(dealToasts.closedLost());
      holdThenDone();
    },
    onError: (err) =>
      setError(apiErrorMessage(err, "Mark as closed-lost failed. Refresh and try again.")),
  });

  const anyPending = markWon.isPending || markLost.isPending;
  const inert = anyPending || finishing;

  // Gate on the rounded CENTS, not the raw float: a sub-cent value like "0.001"
  // is `parseFloat > 0` but rounds to 0 cents → backend `gt=0` 422 (CHP-503/504).
  const valueValid = contractValue !== "" && dollarsToCents(contractValue) > 0;
  const dateValid = paymentDate !== "";
  const poValid = poNumber.trim() !== "";
  const wonReady = valueValid && dateValid && poValid && !inert;
  // "Other" requires an accompanying note (CHP-243); other reasons don't.
  const lostReady = (closeReason !== "other" || closeNotes.trim() !== "") && !inert;

  return (
    <div className="flex flex-col gap-3">
      {mode === null ? (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={inert}
            onClick={() => {
              setError(null);
              setMode("won");
            }}
          >
            Mark as closed-won
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={inert}
            onClick={() => {
              setError(null);
              setMode("lost");
            }}
          >
            Mark as closed-lost
          </Button>
        </div>
      ) : null}

      {mode === "won" ? (
        <div
          ref={formRef}
          role="group"
          aria-labelledby="mark-won-heading"
          className="rounded-md border border-border p-4"
        >
          <h3 id="mark-won-heading" className="text-sm font-semibold">
            Mark &apos;Closed-Won&apos; status for {deal.client_name}?
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Verify the contract value and first payment due date directly from the purchase order.
          </p>
          <label className="mt-2 block text-sm">
            Confirmed contract value (AUD) *
            <CurrencyInput
              value={contractValue}
              onValueChange={setContractValue}
              className="mt-1 block w-full rounded border border-border bg-background p-2 text-sm"
              placeholder="e.g. 500,000"
              required
            />
          </label>
          {/* CHP-396: whole-row click opens the picker. */}
          <label className="mt-2 block text-sm">
            First payment due date *
            <DateRowInput
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              required
              wrapperClassName="mt-1 bg-background"
              className="py-2"
            />
          </label>
          <label className="mt-2 block text-sm">
            Purchase order number *
            <input
              type="text"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              maxLength={100}
              className="mt-1 block w-full rounded border border-border bg-background p-2 text-sm"
              placeholder="Customer purchase order number"
              required
            />
          </label>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              disabled={!wonReady}
              loading={markWon.isPending}
              loadingLabel="Saving…"
              onClick={() => {
                setError(null);
                markWon.mutate();
              }}
            >
              Mark closed-won
            </Button>
            <Button type="button" variant="outline" disabled={anyPending} onClick={cancelForm}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {mode === "lost" ? (
        <div
          ref={formRef}
          role="group"
          aria-labelledby="mark-lost-heading"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-4"
        >
          <h3 id="mark-lost-heading" className="text-sm font-semibold">
            Mark this deal as Closed-Lost for {deal.client_name}?
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            This deal registration ceases upon confirmation and will no longer be active.
          </p>
          <label className="mt-2 block text-sm">
            Reason for closure *
            <select
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value as CloseReasonCategory)}
              className="mt-1 block w-full rounded border border-border bg-background p-2 text-sm"
            >
              {CLOSE_REASONS.map((r) => (
                <option key={r} value={r}>
                  {CLOSE_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
          <label className="mt-2 block text-sm">
            Additional details{closeReason === "other" ? " *" : ""}
            <Textarea
              value={closeNotes}
              onChange={(e) => setCloseNotes(e.target.value)}
              maxLength={1000}
              rows={3}
              className="mt-1 w-full"
            />
          </label>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="destructive"
              disabled={!lostReady}
              loading={markLost.isPending}
              loadingLabel="Saving…"
              onClick={() => {
                setError(null);
                markLost.mutate();
              }}
            >
              Mark closed-lost
            </Button>
            <Button type="button" variant="outline" disabled={anyPending} onClick={cancelForm}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {error ? <FormError>{error}</FormError> : null}
    </div>
  );
}
